
export interface FaqEntry {
  question: string;
  questionAr?: string;
  answer: string;
  answerAr?: string;
  bullets?: string[];
  bulletsAr?: string[];
  outro?: string;
  outroAr?: string;
}

export interface FaqCategory {
  id: string;
  number: string;
  title: string;
  titleAr?: string;
  faqs: FaqEntry[];
}

export const FAQ_CATEGORIES: FaqCategory[] = [
  {
    id: "getting-started",
    number: "01",
    title: "Getting Started",
    titleAr: "البدء",
    faqs: [
      {
        question: "What is DataTram?",
        questionAr: "ما هو DataTram؟",
        answer:
          "DataTram is an AI-powered marketing intelligence platform that brings your sales, website, advertising, organic social, customer, inventory, and competitor data into one place.",
        answerAr:
          "DataTram منصة ذكاء تسويقي مدعومة بالذكاء الاصطناعي تجمع بيانات مبيعاتك وموقعك الإلكتروني وإعلاناتك ووسائل التواصل الاجتماعي العضوية وعملائك ومخزونك ومنافسيك في مكان واحد.",
      },
      {
        question: "Who is DataTram designed for?",
        questionAr: "لمن صُمم DataTram؟",
        answer:
          "DataTram is designed for e-commerce brands, retailers, marketing teams, performance marketers, founders, business managers, and agencies that want simpler, faster access to marketing and sales insights.",
        answerAr:
          "صُمم DataTram لعلامات التجارة الإلكترونية وتجار التجزئة وفرق التسويق ومسوّقي الأداء والمؤسسين ومديري الأعمال والوكالات الراغبين في الوصول إلى رؤى التسويق والمبيعات بشكل أبسط وأسرع.",
      },
      {
        question: "What dashboards are available in DataTram?",
        questionAr: "ما هي اللوحات المتاحة في DataTram؟",
        answer:
          "Depending on your subscription and connected sources, DataTram may include Sales & Product Analytics, Geo-Spatial Analytics, Web Analytics, Organic Media, Paid Media, Customer Lifetime Value, Email Engagement, and Social Listening.",
        answerAr:
          "بحسب اشتراكك والمصادر المتصلة، قد يتضمن DataTram لوحات تحليلات المبيعات والمنتجات، والتحليلات الجغرافية المكانية، وتحليلات الويب، والوسائط العضوية، والوسائط المدفوعة، وقيمة عمر العميل، والتفاعل عبر البريد الإلكتروني، والاستماع الاجتماعي.",
      },
      {
        question: "Which data sources can I connect?",
        questionAr: "ما هي مصادر البيانات التي يمكنني ربطها؟",
        answer:
          "The current interface supports core integrations such as Shopify, Google Analytics, Google Ads, Facebook, and Instagram. Additional integrations may be enabled according to your account configuration.",
        answerAr:
          "تدعم الواجهة الحالية عمليات الربط الأساسية مثل Shopify وGoogle Analytics وGoogle Ads وFacebook وInstagram. قد يتم تفعيل عمليات ربط إضافية بحسب إعدادات حسابك.",
      },
      {
        question: "Where can I see my connected platforms?",
        questionAr: "أين يمكنني رؤية المنصات المتصلة لدي؟",
        answer:
          "Open Sources from the left navigation menu. The Connected Data Sources screen shows the platforms currently linked to your DataTram account.",
        answerAr:
          "افتح \"المصادر\" من قائمة التنقل الجانبية. تعرض شاشة مصادر البيانات المتصلة المنصات المرتبطة حاليًا بحساب DataTram الخاص بك.",
      },
      {
        question: "Do I need technical or BI expertise to use DataTram?",
        questionAr: "هل أحتاج إلى خبرة تقنية أو في ذكاء الأعمال لاستخدام DataTram؟",
        answer:
          "No. DataTram is designed for business users and provides prebuilt dashboards, AI-generated explanations, filters, tooltips, and natural-language querying.",
        answerAr:
          "لا. صُمم DataTram لمستخدمي الأعمال، ويوفر لوحات جاهزة، وشروحًا مُولّدة بالذكاء الاصطناعي، ومرشحات، وتلميحات توضيحية، وإمكانية الاستعلام باللغة الطبيعية.",
      },
      {
        question: "How do I change the reporting period?",
        questionAr: "كيف أغيّر فترة التقرير؟",
        answer:
          "Use the start-date and end-date selectors at the top of the dashboard. The dashboard metrics, charts, comparisons, and AI Executive Summary will update for the selected period.",
        answerAr:
          "استخدم أدوات اختيار تاريخ البداية والنهاية أعلى اللوحة. سيتم تحديث مقاييس اللوحة والرسوم البيانية والمقارنات والملخص التنفيذي بالذكاء الاصطناعي وفقًا للفترة المحددة.",
      },
      {
        question: "What does \"vs previous period\" mean?",
        questionAr: "ماذا يعني \"مقارنة بالفترة السابقة\"؟",
        answer:
          "It compares the selected date range with the immediately preceding equivalent period. For example, a selected 30-day period is compared with the previous 30 days.",
        answerAr:
          "تتم مقارنة النطاق الزمني المحدد بالفترة المكافئة التي تسبقه مباشرة. على سبيل المثال، تتم مقارنة فترة 30 يومًا محددة بالثلاثين يومًا السابقة لها.",
      },
      {
        question: "What do the green and red percentages mean?",
        questionAr: "ماذا تعني النسب المئوية الخضراء والحمراء؟",
        answer:
          "The percentage shows how a metric changed compared with the previous period. The color indicates whether the movement is generally favourable or unfavourable for that metric. For example, an increase in sales may appear green, while a decrease in bounce rate may also appear green.",
        answerAr:
          "توضح النسبة المئوية كيفية تغيّر المقياس مقارنة بالفترة السابقة. يشير اللون إلى ما إذا كان التغيّر إيجابيًا أو سلبيًا بشكل عام لهذا المقياس. على سبيل المثال، قد تظهر زيادة المبيعات باللون الأخضر، وقد يظهر انخفاض معدل الارتداد أيضًا باللون الأخضر.",
      },
      {
        question: "What does the question-mark icon beside a metric mean?",
        questionAr: "ماذا تعني أيقونة علامة الاستفهام بجانب المقياس؟",
        answer:
          "Select or hover over the icon to view the metric's definition, calculation, or additional context.",
        answerAr:
          "انقر على الأيقونة أو مرّر المؤشر فوقها لعرض تعريف المقياس أو طريقة احتسابه أو أي سياق إضافي.",
      },
      {
        question: "How often is my data refreshed?",
        questionAr: "كم مرة يتم تحديث بياناتي؟",
        answer:
          "DataTram refreshes connected data automatically according to the schedule configured for your account and data source. Refresh frequency may differ by integration.",
        answerAr:
          "يقوم DataTram بتحديث البيانات المتصلة تلقائيًا وفقًا للجدول المُهيأ لحسابك ومصدر البيانات. قد يختلف تكرار التحديث باختلاف عملية الربط.",
      },
      {
        question: "Why is a dashboard showing blank charts or zero values?",
        questionAr: "لماذا تعرض اللوحة رسومًا بيانية فارغة أو قيمًا صفرية؟",
        answer: "This can happen when:",
        answerAr: "يمكن أن يحدث هذا عندما:",
        bullets: [
          "No data exists for the selected date range",
          "The connected platform returned no activity",
          "A filter removed all matching records",
          "The source connection requires attention",
          "Data is still being refreshed",
        ],
        bulletsAr: [
          "لا توجد بيانات للنطاق الزمني المحدد",
          "لم تُعِد المنصة المتصلة أي نشاط",
          "أزال أحد المرشحات جميع السجلات المطابقة",
          "يتطلب اتصال المصدر مراجعة",
          "لا تزال البيانات قيد التحديث",
        ],
        outro: "Try expanding the date range, clearing filters, and checking the Sources screen.",
        outroAr: "جرّب توسيع النطاق الزمني، ومسح المرشحات، والتحقق من شاشة المصادر.",
      },
    ],
  },
  {
    id: "sales-product",
    number: "02",
    title: "Sales & Product Analytics",
    titleAr: "تحليلات المبيعات والمنتجات",
    faqs: [
      {
        question: "What does the Sales & Product Analytics dashboard show?",
        questionAr: "ماذا تعرض لوحة تحليلات المبيعات والمنتجات؟",
        answer:
          "It provides a consolidated view of Shopify sales and product performance, including sales, refunds, orders, average order value, customers, sales channels, product performance, inventory, and customer behaviour.",
        answerAr:
          "توفر عرضًا موحدًا لأداء المبيعات والمنتجات على Shopify، بما في ذلك المبيعات والمبالغ المستردة والطلبات ومتوسط قيمة الطلب والعملاء وقنوات البيع وأداء المنتجات والمخزون وسلوك العملاء.",
      },
      {
        question: "Where does the Sales & Product Analytics data come from?",
        questionAr: "من أين تأتي بيانات تحليلات المبيعات والمنتجات؟",
        answer: "The dashboard primarily uses data from the connected Shopify store.",
        answerAr: "تعتمد اللوحة بشكل أساسي على بيانات متجر Shopify المتصل.",
      },
      {
        question: "What is included in the Overview tab?",
        questionAr: "ماذا تتضمن علامة التبويب \"نظرة عامة\"؟",
        answer: "The Overview tab provides a high-level summary of:",
        answerAr: "توفر علامة التبويب \"نظرة عامة\" ملخصًا عامًا لكل مما يلي:",
        bullets: [
          "Total sales",
          "Refunds",
          "Orders",
          "Average order value",
          "Customers who ordered",
          "Sales trends",
          "Sales by channel",
          "Top-performing products",
        ],
        bulletsAr: [
          "إجمالي المبيعات",
          "المبالغ المستردة",
          "الطلبات",
          "متوسط قيمة الطلب",
          "العملاء الذين قاموا بالطلب",
          "اتجاهات المبيعات",
          "المبيعات حسب القناة",
          "المنتجات الأعلى أداءً",
        ],
      },
      {
        question: "What is included in the Sales tab?",
        questionAr: "ماذا تتضمن علامة التبويب \"المبيعات\"؟",
        answer: "The Sales tab provides deeper order and revenue analysis, including:",
        answerAr: "توفر علامة التبويب \"المبيعات\" تحليلًا أعمق للطلبات والإيرادات، بما في ذلك:",
        bullets: [
          "Total sales",
          "Top sales channel",
          "Fulfilled and unfulfilled orders",
          "Sales by channel",
          "Order-level information",
          "Campaign or channel contribution",
        ],
        bulletsAr: [
          "إجمالي المبيعات",
          "أعلى قناة مبيعات",
          "الطلبات المُنفَّذة وغير المُنفَّذة",
          "المبيعات حسب القناة",
          "معلومات على مستوى الطلب",
          "مساهمة الحملة أو القناة",
        ],
      },
      {
        question: "Can I review Shopify orders without opening Shopify?",
        questionAr: "هل يمكنني مراجعة طلبات Shopify دون فتح Shopify؟",
        answer:
          "Yes. DataTram can display order-level information, including the order status and available customer and transaction details.",
        answerAr:
          "نعم. يمكن لـ DataTram عرض معلومات على مستوى الطلب، بما في ذلك حالة الطلب وتفاصيل العميل والمعاملة المتاحة.",
      },
      {
        question: "How can I find a specific order?",
        questionAr: "كيف يمكنني إيجاد طلب معين؟",
        answer:
          "Use the available search and filters to find orders by information such as order number or customer name. You can also filter orders by fulfilment status.",
        answerAr:
          "استخدم البحث والمرشحات المتاحة لإيجاد الطلبات باستخدام معلومات مثل رقم الطلب أو اسم العميل. يمكنك أيضًا تصفية الطلبات حسب حالة التنفيذ.",
      },
      {
        question: "Can I export order information?",
        questionAr: "هل يمكنني تصدير معلومات الطلبات؟",
        answer:
          "Yes. Export options are available on supported tables and reports. The format may vary depending on the screen and report.",
        answerAr:
          "نعم. تتوفر خيارات التصدير في الجداول والتقارير المدعومة. قد تختلف الصيغة بحسب الشاشة والتقرير.",
      },
      {
        question: "What does \"Sales by Channel\" mean?",
        questionAr: "ماذا تعني \"المبيعات حسب القناة\"؟",
        answer:
          "Sales by Channel estimates which source contributed to an order, such as Google, Facebook, Instagram, Bing, direct traffic, or another source.",
        answerAr:
          "تقدّر ميزة المبيعات حسب القناة المصدر الذي ساهم في الطلب، مثل Google أو Facebook أو Instagram أو Bing أو الزيارات المباشرة أو مصدر آخر.",
      },
      {
        question: "Why might DataTram's channel attribution differ from Shopify?",
        questionAr: "لماذا قد يختلف إسناد القناة في DataTram عنه في Shopify؟",
        answer:
          "DataTram may classify orders using referral information and source-matching rules. This may produce different channel assignments from the classifications used directly inside Shopify.",
        answerAr:
          "قد يصنّف DataTram الطلبات باستخدام معلومات الإحالة وقواعد مطابقة المصدر، ما قد يؤدي إلى تخصيص قنوات مختلفة عن التصنيفات المستخدمة مباشرة داخل Shopify.",
      },
      {
        question: "What does the Inventory tab show?",
        questionAr: "ماذا تعرض علامة التبويب \"المخزون\"؟",
        answer: "The Inventory tab shows:",
        answerAr: "تعرض علامة التبويب \"المخزون\":",
        bullets: [
          "Total active products",
          "Products in stock",
          "Products with low stock",
          "Products that are out of stock",
          "Top products by on-hand quantity",
          "Product-level stock counts and statuses",
        ],
        bulletsAr: [
          "إجمالي المنتجات النشطة",
          "المنتجات المتوفرة في المخزون",
          "المنتجات ذات المخزون المنخفض",
          "المنتجات غير المتوفرة",
          "أفضل المنتجات حسب الكمية المتوفرة",
          "أعداد وحالات المخزون على مستوى المنتج",
        ],
      },
      {
        question: "How do I change the low-stock threshold?",
        questionAr: "كيف أغيّر حد المخزون المنخفض؟",
        answer:
          "Enter the required quantity in the Low Stock Threshold field and confirm the change. Products at or below that quantity will be classified as low stock.",
        answerAr:
          "أدخل الكمية المطلوبة في حقل حد المخزون المنخفض وأكّد التغيير. سيتم تصنيف المنتجات التي تساوي هذه الكمية أو تقل عنها على أنها مخزون منخفض.",
      },
      {
        question: "Can each business use a different low-stock threshold?",
        questionAr: "هل يمكن لكل نشاط تجاري استخدام حد مخزون منخفض مختلف؟",
        answer: "Yes. The threshold can be adjusted based on the inventory requirements of your business.",
        answerAr: "نعم. يمكن تعديل الحد بناءً على متطلبات المخزون الخاصة بنشاطك التجاري.",
      },
      {
        question: "How can I find a particular product?",
        questionAr: "كيف يمكنني إيجاد منتج معين؟",
        answer:
          "Use the product search field and available category or stock-status filters to find matching products.",
        answerAr:
          "استخدم حقل البحث عن المنتجات ومرشحات الفئة أو حالة المخزون المتاحة لإيجاد المنتجات المطابقة.",
      },
      {
        question: "What do the inventory statuses mean?",
        questionAr: "ماذا تعني حالات المخزون؟",
        answer: "Typical inventory statuses include:",
        answerAr: "تشمل حالات المخزون النموذجية:",
        bullets: [
          "In Stock: Inventory is above the configured low-stock threshold.",
          "Low Stock: Inventory is at or below the threshold but remains available.",
          "Out of Stock: No available inventory remains.",
        ],
        bulletsAr: [
          "متوفر: المخزون أعلى من حد المخزون المنخفض المُهيأ.",
          "منخفض: المخزون عند الحد أو أقل منه لكنه لا يزال متاحًا.",
          "غير متوفر: لا يوجد مخزون متاح.",
        ],
      },
      {
        question: "What does the Customers tab show?",
        questionAr: "ماذا تعرض علامة التبويب \"العملاء\"؟",
        answer: "The Customers tab provides information such as:",
        answerAr: "توفر علامة التبويب \"العملاء\" معلومات مثل:",
        bullets: [
          "Customers active during the selected period",
          "Customers who placed orders",
          "Average order value",
          "New and returning customers",
          "Customer purchase history",
          "Customer-level segments",
          "Total orders and total spend",
        ],
        bulletsAr: [
          "العملاء النشطون خلال الفترة المحددة",
          "العملاء الذين قدّموا طلبات",
          "متوسط قيمة الطلب",
          "العملاء الجدد والعائدون",
          "سجل مشتريات العميل",
          "شرائح على مستوى العميل",
          "إجمالي الطلبات وإجمالي الإنفاق",
        ],
      },
      {
        question: "What is the difference between a new and returning customer?",
        questionAr: "ما الفرق بين العميل الجديد والعميل العائد؟",
        answer:
          "A new customer placed their first order during the selected period. A returning customer had placed at least one order before the selected period and ordered again.",
        answerAr:
          "العميل الجديد هو من قدّم طلبه الأول خلال الفترة المحددة. أما العميل العائد فهو من قدّم طلبًا واحدًا على الأقل قبل الفترة المحددة وطلب مرة أخرى.",
      },
      {
        question: "How does DataTram segment customers?",
        questionAr: "كيف يقوم DataTram بتقسيم العملاء إلى شرائح؟",
        answer:
          "DataTram uses customer behaviour such as recency, frequency, and monetary value to group customers into actionable segments. Examples may include loyal customers, high spenders, one-time buyers, idle customers, and customers at risk of churn.",
        answerAr:
          "يستخدم DataTram سلوك العميل مثل حداثة الشراء وتكراره وقيمته المالية لتجميع العملاء في شرائح قابلة للتنفيذ. تشمل الأمثلة العملاء المخلصين، وكبار المنفقين، والمشترين لمرة واحدة، والعملاء غير النشطين، والعملاء المعرّضين لخطر التسرب.",
      },
      {
        question: "Can I export customer data?",
        questionAr: "هل يمكنني تصدير بيانات العملاء؟",
        answer:
          "Yes. Supported customer tables and audience lists can be exported using the available download option.",
        answerAr:
          "نعم. يمكن تصدير جداول العملاء وقوائم الجمهور المدعومة باستخدام خيار التنزيل المتاح.",
      },
    ],
  },
  {
    id: "geo-spatial",
    number: "03",
    title: "Geo-Spatial Analytics",
    titleAr: "التحليلات الجغرافية المكانية",
    faqs: [
      {
        question: "What is the Geo-Spatial Analytics dashboard?",
        questionAr: "ما هي لوحة التحليلات الجغرافية المكانية؟",
        answer:
          "It shows how sales, orders, customers, discounts, quantities, and returns vary by geographic location.",
        answerAr:
          "تعرض كيفية تفاوت المبيعات والطلبات والعملاء والخصومات والكميات والمرتجعات حسب الموقع الجغرافي.",
      },
      {
        question: "How is geographic information determined?",
        questionAr: "كيف يتم تحديد المعلومات الجغرافية؟",
        answer:
          "DataTram uses available order-location information to associate transactions with cities, provinces, states, or regions.",
        answerAr:
          "يستخدم DataTram معلومات موقع الطلب المتاحة لربط المعاملات بالمدن أو المحافظات أو الولايات أو المناطق.",
      },
      {
        question: "What information appears for each region?",
        questionAr: "ما المعلومات التي تظهر لكل منطقة؟",
        answer: "Depending on the available data, each region may show:",
        answerAr: "بحسب البيانات المتاحة، قد تعرض كل منطقة:",
        bullets: [
          "Total sales",
          "Orders",
          "Net quantity",
          "Discounts",
          "Returns",
          "Return orders",
          "Top-performing products",
        ],
        bulletsAr: [
          "إجمالي المبيعات",
          "الطلبات",
          "صافي الكمية",
          "الخصومات",
          "المرتجعات",
          "طلبات الإرجاع",
          "المنتجات الأعلى أداءً",
        ],
      },
      {
        question: "What do the pins on the map show?",
        questionAr: "ماذا تعرض الدبابيس على الخريطة؟",
        answer:
          "Selecting or hovering over a map location can show city-level information such as sales, orders, customers, and returns.",
        answerAr:
          "يؤدي النقر على موقع في الخريطة أو تمرير المؤشر فوقه إلى عرض معلومات على مستوى المدينة مثل المبيعات والطلبات والعملاء والمرتجعات.",
      },
      {
        question: "How can the time heat map help me?",
        questionAr: "كيف يمكن أن تساعدني الخريطة الحرارية الزمنية؟",
        answer:
          "The heat map identifies the days and times when customers are most likely to place orders. This can help you schedule campaigns and allocate advertising spend more effectively.",
        answerAr:
          "تحدد الخريطة الحرارية الأيام والأوقات التي يرجّح فيها أن يقوم العملاء بتقديم الطلبات، ما يساعدك على جدولة الحملات وتخصيص الإنفاق الإعلاني بفعالية أكبر.",
      },
      {
        question: "Can I filter the Geo-Spatial dashboard by city or region?",
        questionAr: "هل يمكنني تصفية لوحة التحليلات الجغرافية حسب المدينة أو المنطقة؟",
        answer:
          "Yes. Available location and date filters can be used to focus the dashboard on a particular city, province, state, or region.",
        answerAr:
          "نعم. يمكن استخدام مرشحات الموقع والتاريخ المتاحة لتركيز اللوحة على مدينة أو محافظة أو ولاية أو منطقة معينة.",
      },
    ],
  },
  {
    id: "web-analytics",
    number: "04",
    title: "Web Analytics",
    titleAr: "تحليلات الويب",
    faqs: [
      {
        question: "Where does the Web Analytics data come from?",
        questionAr: "من أين تأتي بيانات تحليلات الويب؟",
        answer: "The Web Analytics dashboard uses data from the connected Google Analytics account.",
        answerAr: "تعتمد لوحة تحليلات الويب على بيانات حساب Google Analytics المتصل.",
      },
      {
        question: "What metrics are available in Web Analytics?",
        questionAr: "ما هي المقاييس المتاحة في تحليلات الويب؟",
        answer: "Metrics may include:",
        answerAr: "قد تشمل المقاييس:",
        bullets: [
          "Total users",
          "Sessions",
          "Bounce rate",
          "Sessions per user",
          "Session duration",
          "Page views",
          "Revenue",
          "Key event rate",
        ],
        bulletsAr: [
          "إجمالي المستخدمين",
          "الجلسات",
          "معدل الارتداد",
          "الجلسات لكل مستخدم",
          "مدة الجلسة",
          "مشاهدات الصفحة",
          "الإيرادات",
          "معدل الأحداث الرئيسية",
        ],
      },
      {
        question: "Can I compare two months?",
        questionAr: "هل يمكنني مقارنة شهرين؟",
        answer:
          "Yes. Use the two month selectors on the Performance Comparison screen to compare website performance across separate periods.",
        answerAr:
          "نعم. استخدم أداتَي اختيار الشهر في شاشة مقارنة الأداء لمقارنة أداء الموقع الإلكتروني عبر فترات منفصلة.",
      },
      {
        question: "Can I analyse website activity by location?",
        questionAr: "هل يمكنني تحليل نشاط الموقع الإلكتروني حسب الموقع الجغرافي؟",
        answer:
          "Yes. Available views may show users, sessions, page views, engagement, and bounce rate by city or other geographic dimensions.",
        answerAr:
          "نعم. قد تعرض العروض المتاحة المستخدمين والجلسات ومشاهدات الصفحة والتفاعل ومعدل الارتداد حسب المدينة أو أبعاد جغرافية أخرى.",
      },
      {
        question: "Can I analyse performance by demographic group?",
        questionAr: "هل يمكنني تحليل الأداء حسب الفئة الديموغرافية؟",
        answer:
          "Where supported by the connected data, DataTram can show performance by dimensions such as age, gender, and device.",
        answerAr:
          "حيثما تدعم البيانات المتصلة ذلك، يمكن لـ DataTram عرض الأداء حسب أبعاد مثل العمر والجنس والجهاز.",
      },
      {
        question: "What is Channel Performance?",
        questionAr: "ما هو أداء القنوات؟",
        answer:
          "Channel Performance shows how website users and activity are distributed across acquisition channels, such as organic search, paid traffic, social traffic, direct traffic, or referrals.",
        answerAr:
          "يعرض أداء القنوات كيفية توزّع مستخدمي الموقع ونشاطه عبر قنوات الاكتساب، مثل البحث العضوي، والزيارات المدفوعة، وزيارات وسائل التواصل الاجتماعي، والزيارات المباشرة، أو الإحالات.",
      },
    ],
  },
  {
    id: "organic-media",
    number: "05",
    title: "Organic Media Analytics",
    titleAr: "تحليلات الوسائط العضوية",
    faqs: [
      {
        question: "What does the Organic Media dashboard cover?",
        questionAr: "ماذا تغطي لوحة الوسائط العضوية؟",
        answer:
          "It provides reporting for connected Facebook and Instagram pages, including content performance and follower insights.",
        answerAr:
          "توفر تقارير لصفحات Facebook وInstagram المتصلة، بما في ذلك أداء المحتوى ورؤى المتابعين.",
      },
      {
        question: "What is included in the Facebook Content Report?",
        questionAr: "ماذا يتضمن تقرير محتوى Facebook؟",
        answer: "The report may include:",
        answerAr: "قد يتضمن التقرير:",
        bullets: [
          "Posts created",
          "Organic reach",
          "Impressions",
          "Clicks",
          "CTR",
          "Engagement rate",
          "Shares",
          "Comments",
          "Reactions",
          "Post-level performance",
        ],
        bulletsAr: [
          "المنشورات التي تم إنشاؤها",
          "الوصول العضوي",
          "مرات الظهور",
          "النقرات",
          "معدل النقر إلى الظهور (CTR)",
          "معدل التفاعل",
          "المشاركات",
          "التعليقات",
          "التفاعلات",
          "الأداء على مستوى المنشور",
        ],
      },
      {
        question: "What is included in the Instagram Content Report?",
        questionAr: "ماذا يتضمن تقرير محتوى Instagram؟",
        answer: "The report may include:",
        answerAr: "قد يتضمن التقرير:",
        bullets: [
          "Posts created",
          "Reach and impressions",
          "Engagement rate",
          "Comments and saves",
          "Reactions",
          "Profile visits",
          "New followers",
          "Post-level performance",
        ],
        bulletsAr: [
          "المنشورات التي تم إنشاؤها",
          "الوصول ومرات الظهور",
          "معدل التفاعل",
          "التعليقات وعمليات الحفظ",
          "التفاعلات",
          "زيارات الملف الشخصي",
          "المتابعون الجدد",
          "الأداء على مستوى المنشور",
        ],
      },
      {
        question: "What does Content Type Breakdown show?",
        questionAr: "ماذا يعرض توزيع أنواع المحتوى؟",
        answer:
          "It compares the number and performance of different formats, such as images, videos, reels, and carousels.",
        answerAr:
          "يقارن عدد وأداء صيغ المحتوى المختلفة، مثل الصور ومقاطع الفيديو والريلز والمنشورات الدائرية.",
      },
      {
        question: "Can I see the performance of an individual post?",
        questionAr: "هل يمكنني رؤية أداء منشور فردي؟",
        answer:
          "Yes. The post-level table provides available metrics for individual posts and may include a link to open the original post.",
        answerAr:
          "نعم. يوفر جدول مستوى المنشور المقاييس المتاحة للمنشورات الفردية، وقد يتضمن رابطًا لفتح المنشور الأصلي.",
      },
      {
        question: "What do the follower reports show?",
        questionAr: "ماذا تعرض تقارير المتابعين؟",
        answer: "Follower reports may include:",
        answerAr: "قد تتضمن تقارير المتابعين:",
        bullets: [
          "Total followers",
          "New followers",
          "Follower growth trends",
          "Followers by country or city",
          "Age and gender distribution",
        ],
        bulletsAr: [
          "إجمالي المتابعين",
          "المتابعون الجدد",
          "اتجاهات نمو المتابعين",
          "المتابعون حسب الدولة أو المدينة",
          "التوزيع حسب العمر والجنس",
        ],
      },
    ],
  },
  {
    id: "paid-media",
    number: "06",
    title: "Paid Media Analytics",
    titleAr: "تحليلات الوسائط المدفوعة",
    faqs: [
      {
        question: "Which advertising platforms are covered?",
        questionAr: "ما هي منصات الإعلان المشمولة؟",
        answer:
          "The Paid Media dashboard provides dedicated reporting for connected Meta Ads and Google Ads accounts.",
        answerAr:
          "توفر لوحة الوسائط المدفوعة تقارير مخصصة لحسابات Meta Ads وGoogle Ads المتصلة.",
      },
      {
        question: "What does the Meta Ads dashboard show?",
        questionAr: "ماذا تعرض لوحة Meta Ads؟",
        answer: "It may include:",
        answerAr: "قد تتضمن:",
        bullets: [
          "Total spend",
          "Impressions",
          "Link clicks",
          "Purchases",
          "Click-through rate",
          "Cost per click",
          "Cost per purchase",
          "Purchase ROAS",
          "Conversion funnel",
          "Campaign, ad-set, and ad-level performance",
        ],
        bulletsAr: [
          "إجمالي الإنفاق",
          "مرات الظهور",
          "نقرات الروابط",
          "المشتريات",
          "معدل النقر إلى الظهور",
          "تكلفة النقرة",
          "تكلفة الشراء",
          "العائد على الإنفاق الإعلاني للمشتريات (ROAS)",
          "قمع التحويل",
          "الأداء على مستوى الحملة والمجموعة الإعلانية والإعلان",
        ],
      },
      {
        question: "What does the Google Ads dashboard show?",
        questionAr: "ماذا تعرض لوحة Google Ads؟",
        answer: "It may include:",
        answerAr: "قد تتضمن:",
        bullets: [
          "Advertising cost",
          "Impressions",
          "Clicks",
          "Conversions",
          "CTR",
          "CPC",
          "CPA",
          "ROAS",
          "Campaign and ad-group performance",
        ],
        bulletsAr: [
          "تكلفة الإعلان",
          "مرات الظهور",
          "النقرات",
          "التحويلات",
          "معدل النقر إلى الظهور (CTR)",
          "تكلفة النقرة (CPC)",
          "تكلفة الاكتساب (CPA)",
          "العائد على الإنفاق الإعلاني (ROAS)",
          "الأداء على مستوى الحملة والمجموعة الإعلانية",
        ],
      },
      {
        question: "What does the Conversion Funnel show?",
        questionAr: "ماذا يعرض قمع التحويل؟",
        answer:
          "The funnel visualises how users progress from advertising spend to impressions, clicks, and conversions. It helps identify where the largest drop-offs occur.",
        answerAr:
          "يوضّح القمع كيفية تقدّم المستخدمين من الإنفاق الإعلاني إلى مرات الظهور والنقرات والتحويلات، ما يساعد على تحديد أماكن حدوث أكبر معدلات الانسحاب.",
      },
      {
        question: "Can I switch individual trend metrics on or off?",
        questionAr: "هل يمكنني تفعيل أو إيقاف مقاييس اتجاه معينة؟",
        answer:
          "Yes. Where metric selectors are available, you can choose which measures - such as spend, impressions, clicks, or conversions - appear in the chart.",
        answerAr:
          "نعم. حيثما تتوفر أدوات اختيار المقاييس، يمكنك اختيار المقاييس التي تظهر في الرسم البياني، مثل الإنفاق أو مرات الظهور أو النقرات أو التحويلات.",
      },
      {
        question: "Can I compare performance across Meta placements?",
        questionAr: "هل يمكنني مقارنة الأداء عبر مواضع إعلانات Meta؟",
        answer:
          "Where the data is available, DataTram can compare performance across placements such as Facebook, Instagram, Audience Network, and other supported Meta placements.",
        answerAr:
          "حيثما تتوفر البيانات، يمكن لـ DataTram مقارنة الأداء عبر مواضع مثل Facebook وInstagram وAudience Network وغيرها من مواضع Meta المدعومة.",
      },
      {
        question: "Can I review campaign, ad-set, and ad-group details?",
        questionAr: "هل يمكنني مراجعة تفاصيل الحملة والمجموعة الإعلانية؟",
        answer:
          "Yes. Detailed tables allow users to drill from campaigns into lower-level structures such as Meta ad sets or Google ad groups.",
        answerAr:
          "نعم. تتيح الجداول التفصيلية للمستخدمين التعمق من الحملات إلى مستويات أدق مثل مجموعات إعلانات Meta أو مجموعات إعلانات Google.",
      },
    ],
  },
  {
    id: "cltv",
    number: "07",
    title: "Customer Lifetime Value and Predictive Analytics",
    titleAr: "قيمة عمر العميل والتحليلات التنبؤية",
    faqs: [
      {
        question: "What is the CLTV dashboard?",
        questionAr: "ما هي لوحة قيمة عمر العميل؟",
        answer:
          "The Customer Lifetime Value dashboard estimates customer value, retention behaviour, predicted revenue, and churn risk.",
        answerAr:
          "تقدّر لوحة قيمة عمر العميل قيمة العميل، وسلوك الاحتفاظ به، والإيرادات المتوقعة، ومخاطر التسرب.",
      },
      {
        question: "What is Customer Lifetime Value?",
        questionAr: "ما هي قيمة عمر العميل؟",
        answer:
          "Customer Lifetime Value estimates the total value a customer is expected to generate over their relationship with the business.",
        answerAr:
          "تقدّر قيمة عمر العميل إجمالي القيمة المتوقع أن يحققها العميل طوال علاقته بالنشاط التجاري.",
      },
      {
        question: "What is churn risk?",
        questionAr: "ما هي مخاطر التسرب؟",
        answer:
          "Churn risk estimates the likelihood that a customer may stop purchasing. Customers may be grouped into high-, medium-, or low-risk categories.",
        answerAr:
          "تقدّر مخاطر التسرب احتمالية توقف العميل عن الشراء. قد يتم تصنيف العملاء ضمن فئات مخاطر عالية أو متوسطة أو منخفضة.",
      },
      {
        question: "What is predicted revenue?",
        questionAr: "ما هي الإيرادات المتوقعة؟",
        answer:
          "Predicted revenue is an estimate of future sales based on historical customer behaviour and available predictive models.",
        answerAr:
          "الإيرادات المتوقعة هي تقدير للمبيعات المستقبلية استنادًا إلى سلوك العملاء التاريخي والنماذج التنبؤية المتاحة.",
      },
      {
        question: "What does Segment Evolution show?",
        questionAr: "ماذا يعرض تطور الشرائح؟",
        answer:
          "Segment Evolution shows how customer groups - such as loyal customers, high spenders, idle customers, or one-time buyers - change over time.",
        answerAr:
          "يعرض تطور الشرائح كيفية تغيّر مجموعات العملاء - مثل العملاء المخلصين، وكبار المنفقين، والعملاء غير النشطين، أو المشترين لمرة واحدة - بمرور الوقت.",
      },
      {
        question: "Can I target customers based on CLTV or churn?",
        questionAr: "هل يمكنني استهداف العملاء بناءً على قيمة العمر أو التسرب؟",
        answer:
          "Yes. Customer segments and predictive insights can be used to create audience lists for retention, reactivation, loyalty, or upsell campaigns.",
        answerAr:
          "نعم. يمكن استخدام شرائح العملاء والرؤى التنبؤية لإنشاء قوائم جمهور لحملات الاحتفاظ أو إعادة التنشيط أو الولاء أو البيع الإضافي.",
      },
    ],
  },
  {
    id: "ai-summary",
    number: "08",
    title: "AI Executive Summary",
    titleAr: "الملخص التنفيذي بالذكاء الاصطناعي",
    faqs: [
      {
        question: "What is the AI Executive Summary?",
        questionAr: "ما هو الملخص التنفيذي بالذكاء الاصطناعي؟",
        answer:
          "The AI Executive Summary converts dashboard data into a plain-language explanation of key changes, insights, risks, and recommended actions.",
        answerAr:
          "يحوّل الملخص التنفيذي بالذكاء الاصطناعي بيانات اللوحة إلى شرح بلغة مبسطة للتغيرات الرئيسية والرؤى والمخاطر والإجراءات الموصى بها.",
      },
      {
        question: "Why does the AI Executive Summary change when I change the date?",
        questionAr: "لماذا يتغير الملخص التنفيذي عند تغيير التاريخ؟",
        answer:
          "The summary is generated using the data currently displayed on the dashboard. Changing the reporting period changes the underlying metrics and comparisons.",
        answerAr:
          "يتم إنشاء الملخص باستخدام البيانات المعروضة حاليًا في اللوحة. يؤدي تغيير فترة التقرير إلى تغيير المقاييس والمقارنات الأساسية.",
      },
      {
        question: "How do I view the complete summary?",
        questionAr: "كيف يمكنني عرض الملخص الكامل؟",
        answer: "Select See more or use the expand arrow in the summary panel.",
        answerAr: "اختر \"عرض المزيد\" أو استخدم سهم التوسيع في لوحة الملخص.",
      },
      {
        question: "Why is the AI Executive Summary still loading?",
        questionAr: "لماذا لا يزال الملخص التنفيذي قيد التحميل؟",
        answer:
          "The summary may still be processing the selected data. Wait briefly and refresh the dashboard if it does not appear.",
        answerAr:
          "قد يكون الملخص لا يزال يعالج البيانات المحددة. انتظر لحظة وأعد تحميل اللوحة إذا لم يظهر.",
      },
      {
        question: "What should I do if the summary is unavailable?",
        questionAr: "ماذا أفعل إذا لم يكن الملخص متاحًا؟",
        answer: "Check that:",
        answerAr: "تحقق مما يلي:",
        bullets: [
          "The dashboard contains data",
          "The selected date range is valid",
          "The underlying source is connected",
          "The data refresh has completed",
        ],
        bulletsAr: [
          "تحتوي اللوحة على بيانات",
          "النطاق الزمني المحدد صالح",
          "المصدر الأساسي متصل",
          "اكتمل تحديث البيانات",
        ],
      },
      {
        question: "Should I treat AI recommendations as final decisions?",
        questionAr: "هل ينبغي أن أعتبر توصيات الذكاء الاصطناعي قرارات نهائية؟",
        answer:
          "AI recommendations should be used as decision support. Review important financial, operational, or campaign decisions against the underlying dashboard data and your business context.",
        answerAr:
          "ينبغي استخدام توصيات الذكاء الاصطناعي كدعم لاتخاذ القرار. راجع القرارات المالية أو التشغيلية أو المتعلقة بالحملات المهمة مقارنة ببيانات اللوحة الأساسية وسياق عملك.",
      },
    ],
  },
  {
    id: "ask-datatram",
    number: "09",
    title: "Ask DataTram",
    titleAr: "اسأل DataTram",
    faqs: [
      {
        question: "What is Ask DataTram?",
        questionAr: "ما هو \"اسأل DataTram\"؟",
        answer:
          "Ask DataTram is a conversational analytics assistant that lets you ask questions about your connected dashboards and data in plain English.",
        answerAr:
          "\"اسأل DataTram\" مساعد تحليلات تفاعلي يتيح لك طرح أسئلة حول لوحاتك وبياناتك المتصلة بلغة مبسطة.",
      },
      {
        question: "What kinds of questions can I ask?",
        questionAr: "ما أنواع الأسئلة التي يمكنني طرحها؟",
        answer: "Examples include:",
        answerAr: "تشمل الأمثلة:",
        bullets: [
          "What were my total sales in the last 30 days?",
          "Which channel generated the most revenue?",
          "Which campaigns had the highest ROAS?",
          "Which customers are likely to churn?",
          "Which products are running low on stock?",
          "How did website performance change this month?",
        ],
        bulletsAr: [
          "ما هو إجمالي مبيعاتي خلال آخر 30 يومًا؟",
          "أي قناة حققت أعلى إيرادات؟",
          "أي الحملات حققت أعلى عائد على الإنفاق الإعلاني؟",
          "أي العملاء من المرجّح أن يتسربوا؟",
          "أي المنتجات ينخفض مخزونها؟",
          "كيف تغيّر أداء الموقع الإلكتروني هذا الشهر؟",
        ],
      },
      {
        question: "Can I upload a dashboard screenshot?",
        questionAr: "هل يمكنني رفع لقطة شاشة للوحة؟",
        answer:
          "Yes. You can attach a supported image and ask DataTram to explain the trends, metrics, or potential issues shown in the screenshot.",
        answerAr:
          "نعم. يمكنك إرفاق صورة مدعومة وسؤال DataTram عن شرح الاتجاهات أو المقاييس أو المشكلات المحتملة الظاهرة في لقطة الشاشة.",
      },
      {
        question: "Which attachment formats are supported?",
        questionAr: "ما صيغ المرفقات المدعومة؟",
        answer:
          "The current interface supports common image formats such as JPEG, JPG, and PNG. Upload limits may apply.",
        answerAr:
          "تدعم الواجهة الحالية صيغ الصور الشائعة مثل JPEG وJPG وPNG. قد تُطبَّق حدود للرفع.",
      },
      {
        question: "Can Ask DataTram help create a campaign?",
        questionAr: "هل يمكن لـ \"اسأل DataTram\" المساعدة في إنشاء حملة؟",
        answer:
          "Yes. Ask DataTram can assist with generating a campaign concept, creating email content, or preparing a target audience based on your request.",
        answerAr:
          "نعم. يمكن لـ \"اسأل DataTram\" المساعدة في توليد فكرة حملة، أو إنشاء محتوى بريد إلكتروني، أو إعداد جمهور مستهدف بناءً على طلبك.",
      },
      {
        question: "Can Ask DataTram create a customer list?",
        questionAr: "هل يمكن لـ \"اسأل DataTram\" إنشاء قائمة عملاء؟",
        answer:
          "Yes. You can describe the customers you want to target, such as customers who have not ordered recently, and DataTram can help prepare a matching segment where the required data is available.",
        answerAr:
          "نعم. يمكنك وصف العملاء الذين تريد استهدافهم، مثل العملاء الذين لم يطلبوا مؤخرًا، ويمكن لـ DataTram المساعدة في إعداد شريحة مطابقة حيثما تتوفر البيانات المطلوبة.",
      },
      {
        question: "Is my chat history saved?",
        questionAr: "هل يتم حفظ سجل محادثتي؟",
        answer:
          "Your previous conversations may be retained within the Ask DataTram interface so you can continue earlier analysis.",
        answerAr:
          "قد يتم الاحتفاظ بمحادثاتك السابقة ضمن واجهة \"اسأل DataTram\" لتتمكن من متابعة تحليل سابق.",
      },
      {
        question: "What does \"Clear Chat\" do?",
        questionAr: "ماذا تفعل ميزة \"مسح المحادثة\"؟",
        answer:
          "It removes the messages currently displayed in the conversation. Only use it when you no longer need the current chat context.",
        answerAr:
          "تزيل الرسائل المعروضة حاليًا في المحادثة. استخدمها فقط عندما لا تعد بحاجة إلى سياق المحادثة الحالي.",
      },
      {
        question: "How can I get a better answer from Ask DataTram?",
        questionAr: "كيف يمكنني الحصول على إجابة أفضل من \"اسأل DataTram\"؟",
        answer: "Include:",
        answerAr: "أدرج:",
        bullets: [
          "The metric you want",
          "The required period",
          "Any channel, product, campaign, customer, or location filter",
          "The type of output you need",
        ],
        bulletsAr: [
          "المقياس الذي تريده",
          "الفترة المطلوبة",
          "أي مرشح للقناة أو المنتج أو الحملة أو العميل أو الموقع",
          "نوع المخرجات التي تحتاجها",
        ],
        outro:
          "For example: \"Compare Meta and Google Ads ROAS for June and explain the three biggest changes.\"",
        outroAr:
          "على سبيل المثال: \"قارن العائد على الإنفاق الإعلاني بين Meta وGoogle Ads لشهر يونيو واشرح أكبر ثلاثة تغيرات.\"",
      },
    ],
  },
  {
    id: "engagements",
    number: "10",
    title: "Engagements and Email Campaigns",
    titleAr: "التفاعلات وحملات البريد الإلكتروني",
    faqs: [
      {
        question: "What is the Engagements module?",
        questionAr: "ما هي وحدة التفاعلات؟",
        answer:
          "The Engagements module helps you create audiences, generate email campaigns, send targeted communications, and monitor campaign performance.",
        answerAr:
          "تساعدك وحدة التفاعلات على إنشاء الجماهير، وتوليد حملات البريد الإلكتروني، وإرسال اتصالات مستهدفة، ومتابعة أداء الحملات.",
      },
      {
        question: "What metrics appear in the Engagement Overview?",
        questionAr: "ما المقاييس التي تظهر في نظرة عامة على التفاعلات؟",
        answer: "The overview may show:",
        answerAr: "قد تعرض النظرة العامة:",
        bullets: [
          "Emails sent",
          "Emails opened",
          "Clicks",
          "Conversions",
          "Open rate",
          "Campaign-level performance trends",
        ],
        bulletsAr: [
          "الرسائل المُرسلة",
          "الرسائل المفتوحة",
          "النقرات",
          "التحويلات",
          "معدل الفتح",
          "اتجاهات الأداء على مستوى الحملة",
        ],
      },
      {
        question: "How do I create a new campaign?",
        questionAr: "كيف أنشئ حملة جديدة؟",
        answer:
          "Open Engagements > Campaigns and select Create New Campaign. Follow the steps to choose an audience, prepare the email, review it, and schedule or send it.",
        answerAr:
          "افتح التفاعلات > الحملات واختر \"إنشاء حملة جديدة\". اتبع الخطوات لاختيار الجمهور، وإعداد البريد الإلكتروني، ومراجعته، وجدولته أو إرساله.",
      },
      {
        question: "Can DataTram generate an email using AI?",
        questionAr: "هل يمكن لـ DataTram توليد بريد إلكتروني باستخدام الذكاء الاصطناعي؟",
        answer:
          "Yes. DataTram can generate campaign content based on your audience, objective, offer, and business context.",
        answerAr:
          "نعم. يمكن لـ DataTram توليد محتوى الحملة بناءً على جمهورك وهدفك وعرضك وسياق عملك.",
      },
      {
        question: "Can I edit an AI-generated email before sending it?",
        questionAr: "هل يمكنني تعديل بريد إلكتروني تم توليده بالذكاء الاصطناعي قبل إرساله؟",
        answer:
          "Yes. AI-generated emails should be reviewed in the email editor before they are approved or sent.",
        answerAr:
          "نعم. ينبغي مراجعة رسائل البريد الإلكتروني المُولّدة بالذكاء الاصطناعي في محرر البريد الإلكتروني قبل اعتمادها أو إرسالها.",
      },
      {
        question: "What is the Audience screen?",
        questionAr: "ما هي شاشة الجمهور؟",
        answer:
          "The Audience screen contains customer lists and segments that can be used for targeted campaigns.",
        answerAr:
          "تحتوي شاشة الجمهور على قوائم العملاء والشرائح التي يمكن استخدامها للحملات المستهدفة.",
      },
      {
        question: "How do I create a list or segment?",
        questionAr: "كيف أنشئ قائمة أو شريحة؟",
        answer:
          "Open Engagements > Audience and select Create List / Segment. You can create a list manually or define customers using behavioural and business criteria.",
        answerAr:
          "افتح التفاعلات > الجمهور واختر \"إنشاء قائمة / شريحة\". يمكنك إنشاء قائمة يدويًا أو تحديد العملاء باستخدام معايير سلوكية وتجارية.",
      },
      {
        question: "What is the difference between a list and a Data Science segment?",
        questionAr: "ما الفرق بين القائمة وشريحة علوم البيانات؟",
        answer:
          "A list is generally created manually or through selected rules. A Data Science segment is generated using customer behaviour or predictive analysis, such as churn likelihood.",
        answerAr:
          "تُنشأ القائمة عادةً يدويًا أو عبر قواعد محددة. أما شريحة علوم البيانات فتُولَّد باستخدام سلوك العميل أو التحليل التنبؤي، مثل احتمالية التسرب.",
      },
      {
        question: "Can I send emails to customers likely to churn?",
        questionAr: "هل يمكنني إرسال رسائل بريد إلكتروني للعملاء المرجّح تسربهم؟",
        answer:
          "Yes. Where a churn segment is available, it can be used as the target audience for a retention or reactivation campaign.",
        answerAr:
          "نعم. حيثما تتوفر شريحة التسرب، يمكن استخدامها كجمهور مستهدف لحملة احتفاظ أو إعادة تنشيط.",
      },
      {
        question: "Does DataTram currently support WhatsApp or SMS campaigns?",
        questionAr: "هل يدعم DataTram حاليًا حملات WhatsApp أو الرسائل النصية؟",
        answer:
          "The current engagement module focuses on email. WhatsApp and SMS engagement may be introduced as additional channels.",
        answerAr:
          "تركز وحدة التفاعلات الحالية على البريد الإلكتروني. قد يتم إدخال التفاعل عبر WhatsApp والرسائل النصية كقنوات إضافية لاحقًا.",
      },
    ],
  },
  {
    id: "social-listening",
    number: "11",
    title: "Social Listening Agent",
    titleAr: "وكيل الاستماع الاجتماعي",
    faqs: [
      {
        question: "What is the Social Listening Agent?",
        questionAr: "ما هو وكيل الاستماع الاجتماعي؟",
        answer:
          "It is an AI-powered tool for analysing brand and competitor conversations across supported digital platforms.",
        answerAr:
          "هو أداة مدعومة بالذكاء الاصطناعي لتحليل محادثات العلامة التجارية والمنافسين عبر المنصات الرقمية المدعومة.",
      },
      {
        question: "Can I analyse my own brand?",
        questionAr: "هل يمكنني تحليل علامتي التجارية الخاصة؟",
        answer:
          "Yes. You can use the Social Listening Agent to understand sentiment, recurring themes, customer feedback, and conversations relating to your own brand.",
        answerAr:
          "نعم. يمكنك استخدام وكيل الاستماع الاجتماعي لفهم المشاعر والمواضيع المتكررة وملاحظات العملاء والمحادثات المتعلقة بعلامتك التجارية.",
      },
      {
        question: "Can I analyse competitors?",
        questionAr: "هل يمكنني تحليل المنافسين؟",
        answer:
          "Yes. The agent can compare competitor activity, customer sentiment, strengths, weaknesses, and recurring discussion themes.",
        answerAr:
          "نعم. يمكن للوكيل مقارنة نشاط المنافسين ومشاعر العملاء ونقاط القوة والضعف ومواضيع النقاش المتكررة.",
      },
      {
        question: "Which platforms can be analysed?",
        questionAr: "ما المنصات التي يمكن تحليلها؟",
        answer:
          "Available sources may include Facebook, Instagram, TikTok, reviews, and other supported public channels. Coverage depends on the selected brand, region, and available data.",
        answerAr:
          "قد تشمل المصادر المتاحة Facebook وInstagram وTikTok والمراجعات وقنوات عامة أخرى مدعومة. تعتمد التغطية على العلامة التجارية والمنطقة المحددة والبيانات المتاحة.",
      },
      {
        question: "What does sentiment analysis mean?",
        questionAr: "ماذا يعني تحليل المشاعر؟",
        answer:
          "Sentiment analysis groups relevant comments or mentions into categories such as positive, negative, or neutral.",
        answerAr:
          "يصنّف تحليل المشاعر التعليقات أو الإشارات ذات الصلة إلى فئات مثل إيجابية أو سلبية أو محايدة.",
      },
      {
        question: "Can I see the source behind an insight?",
        questionAr: "هل يمكنني رؤية المصدر وراء رؤية معينة؟",
        answer:
          "Where available, DataTram provides supporting links or references so you can review the original post, comment, or source.",
        answerAr:
          "حيثما يتوفر ذلك، يوفر DataTram روابط أو مراجع داعمة لتتمكن من مراجعة المنشور أو التعليق أو المصدر الأصلي.",
      },
      {
        question: "Can I analyse different countries or regions?",
        questionAr: "هل يمكنني تحليل دول أو مناطق مختلفة؟",
        answer: "Yes. The Social Listening Agent supports regional analysis where relevant data is available.",
        answerAr: "نعم. يدعم وكيل الاستماع الاجتماعي التحليل الإقليمي حيثما تتوفر البيانات ذات الصلة.",
      },
    ],
  },
  {
    id: "notifications",
    number: "12",
    title: "Notifications",
    titleAr: "الإشعارات",
    faqs: [
      {
        question: "What are DataTram notifications?",
        questionAr: "ما هي إشعارات DataTram؟",
        answer:
          "Notifications automatically alert you when an important metric changes or crosses a threshold that you have configured.",
        answerAr:
          "تنبهك الإشعارات تلقائيًا عندما يتغير مقياس مهم أو يتجاوز حدًا قمت بتهيئته.",
      },
      {
        question: "Which types of metrics can I monitor?",
        questionAr: "ما أنواع المقاييس التي يمكنني مراقبتها؟",
        answer: "Depending on the dashboard and account configuration, you may monitor areas such as:",
        answerAr: "بحسب اللوحة وإعدادات الحساب، يمكنك مراقبة مجالات مثل:",
        bullets: [
          "Sales performance",
          "Advertising performance",
          "Website performance",
          "Organic social performance",
          "Inventory",
          "Customer activity",
        ],
        bulletsAr: [
          "أداء المبيعات",
          "أداء الإعلانات",
          "أداء الموقع الإلكتروني",
          "أداء الوسائط الاجتماعية العضوية",
          "المخزون",
          "نشاط العملاء",
        ],
      },
      {
        question: "How can notifications be delivered?",
        questionAr: "كيف يمكن تسليم الإشعارات؟",
        answer: "Available delivery methods may include:",
        answerAr: "قد تشمل طرق التسليم المتاحة:",
        bullets: ["In-app notifications", "Browser push notifications", "Email notifications"],
        bulletsAr: ["إشعارات داخل التطبيق", "إشعارات المتصفح الفورية", "إشعارات البريد الإلكتروني"],
      },
      {
        question: "How do I configure a notification threshold?",
        questionAr: "كيف أهيئ حد إشعار؟",
        answer:
          "Open Account Settings > Notification Preferences, select the metric, define the condition or threshold, choose the delivery channels, and enable the alert.",
        answerAr:
          "افتح إعدادات الحساب > تفضيلات الإشعارات، واختر المقياس، وحدد الشرط أو الحد، واختر قنوات التسليم، وفعّل التنبيه.",
      },
      {
        question: "Can I create an alert for a specific city or product?",
        questionAr: "هل يمكنني إنشاء تنبيه لمدينة أو منتج معين؟",
        answer:
          "Where supported, alerts can be scoped to dimensions such as a city, product, campaign, or channel.",
        answerAr:
          "حيثما يكون ذلك مدعومًا، يمكن تحديد نطاق التنبيهات وفق أبعاد مثل المدينة أو المنتج أو الحملة أو القناة.",
      },
      {
        question: "Where can I view previous notifications?",
        questionAr: "أين يمكنني عرض الإشعارات السابقة؟",
        answer:
          "Open Account Settings > All Notifications. You can view notifications for today, this week, last week, or a custom date range.",
        answerAr:
          "افتح إعدادات الحساب > جميع الإشعارات. يمكنك عرض إشعارات اليوم أو هذا الأسبوع أو الأسبوع الماضي أو نطاق تاريخ مخصص.",
      },
      {
        question: "Why does the Notifications screen say no notifications are available?",
        questionAr: "لماذا تُظهر شاشة الإشعارات عدم توفر إشعارات؟",
        answer:
          "No configured alert may have been triggered during the selected period. Try another date range or review your Notification Preferences.",
        answerAr:
          "قد لا يكون أي تنبيه مُهيأ قد تم تشغيله خلال الفترة المحددة. جرّب نطاق تاريخ آخر أو راجع تفضيلات الإشعارات الخاصة بك.",
      },
      {
        question: "Can I disable a notification?",
        questionAr: "هل يمكنني تعطيل إشعار؟",
        answer: "Yes. Open Notification Preferences and turn off the relevant rule or delivery channel.",
        answerAr: "نعم. افتح تفضيلات الإشعارات وأوقف القاعدة أو قناة التسليم المعنية.",
      },
    ],
  },
  {
    id: "user-management",
    number: "13",
    title: "User Management and Account Settings",
    titleAr: "إدارة المستخدمين وإعدادات الحساب",
    faqs: [
      {
        question: "Who can manage users?",
        questionAr: "من يمكنه إدارة المستخدمين؟",
        answer:
          "Users with the appropriate administrative access can add, edit, activate, deactivate, or remove users.",
        answerAr:
          "يمكن للمستخدمين ذوي صلاحيات الإدارة المناسبة إضافة المستخدمين أو تعديلهم أو تفعيلهم أو تعطيلهم أو إزالتهم.",
      },
      {
        question: "How do I add a user?",
        questionAr: "كيف أضيف مستخدمًا؟",
        answer:
          "Open User Management and select Add User. Enter the user's details and assign the appropriate access level.",
        answerAr:
          "افتح إدارة المستخدمين واختر \"إضافة مستخدم\". أدخل بيانات المستخدم وحدد مستوى الوصول المناسب.",
      },
      {
        question: "How many users are included?",
        questionAr: "كم عدد المستخدمين المشمولين؟",
        answer:
          "Each tenant is configured with a default user allowance. The transcript indicates five users by default, while additional seats may depend on the subscription plan.",
        answerAr:
          "يتم تهيئة كل حساب مستأجر بحد افتراضي للمستخدمين. يشير الإعداد الافتراضي إلى خمسة مستخدمين، بينما قد تعتمد المقاعد الإضافية على خطة الاشتراك.",
      },
      {
        question: "What access levels are available?",
        questionAr: "ما مستويات الوصول المتاحة؟",
        answer: "The current interface includes access levels such as:",
        answerAr: "تتضمن الواجهة الحالية مستويات وصول مثل:",
        bullets: [
          "Admin: Can manage account-level settings and users.",
          "Viewer: Can access permitted dashboards and reports without administrative control.",
        ],
        bulletsAr: [
          "مسؤول: يمكنه إدارة إعدادات الحساب والمستخدمين.",
          "مشاهد: يمكنه الوصول إلى اللوحات والتقارير المسموح بها دون صلاحيات إدارية.",
        ],
      },
      {
        question: "How do I change a user's access level?",
        questionAr: "كيف أغيّر مستوى وصول مستخدم؟",
        answer:
          "Open User Management and select the required role from the Access Level dropdown beside the user.",
        answerAr:
          "افتح إدارة المستخدمين واختر الدور المطلوب من القائمة المنسدلة لمستوى الوصول بجانب المستخدم.",
      },
      {
        question: "Can I temporarily deactivate a user?",
        questionAr: "هل يمكنني تعطيل مستخدم مؤقتًا؟",
        answer:
          "Yes. Use the status toggle to deactivate the account without immediately deleting the user.",
        answerAr:
          "نعم. استخدم مفتاح الحالة لتعطيل الحساب دون حذف المستخدم فورًا.",
      },
      {
        question: "How do I update my profile?",
        questionAr: "كيف أحدّث ملفي الشخصي؟",
        answer:
          "Open Account Settings > My Account. You can update supported profile details such as your name, website, industry, contact details, and profile photo.",
        answerAr:
          "افتح إعدادات الحساب > حسابي. يمكنك تحديث تفاصيل الملف الشخصي المدعومة مثل اسمك وموقعك الإلكتروني ومجال عملك وبيانات الاتصال وصورة الملف الشخصي.",
      },
      {
        question: "How do I change my password?",
        questionAr: "كيف أغيّر كلمة المرور الخاصة بي؟",
        answer:
          "Open Account Settings > My Account, enter your current password, set and confirm the new password, and select Update Password.",
        answerAr:
          "افتح إعدادات الحساب > حسابي، وأدخل كلمة المرور الحالية، وحدد كلمة المرور الجديدة وأكّدها، ثم اختر \"تحديث كلمة المرور\".",
      },
    ],
  },
  {
    id: "troubleshooting",
    number: "14",
    title: "Troubleshooting",
    titleAr: "استكشاف الأخطاء وإصلاحها",
    faqs: [
      {
        question: "What should I do if a connected source stops updating?",
        questionAr: "ماذا أفعل إذا توقف مصدر متصل عن التحديث؟",
        answer:
          "Open the Sources screen and check the connection. The integration may require reconnection, renewed permissions, or assistance from your administrator.",
        answerAr:
          "افتح شاشة المصادر وتحقق من الاتصال. قد تتطلب عملية الربط إعادة الاتصال أو تجديد الصلاحيات أو مساعدة من مسؤول حسابك.",
      },
      {
        question: "Why does DataTram show a different value from the original platform?",
        questionAr: "لماذا يعرض DataTram قيمة مختلفة عن المنصة الأصلية؟",
        answer: "Differences can be caused by:",
        answerAr: "يمكن أن تنتج الاختلافات عن:",
        bullets: [
          "Time-zone settings",
          "Date boundaries",
          "Attribution rules",
          "Currency handling",
          "Data refresh timing",
          "Platform-specific metric definitions",
          "Refund, cancellation, or fulfilment treatment",
        ],
        bulletsAr: [
          "إعدادات المنطقة الزمنية",
          "حدود التاريخ",
          "قواعد الإسناد",
          "معالجة العملة",
          "توقيت تحديث البيانات",
          "تعريفات المقاييس الخاصة بكل منصة",
          "معالجة المبالغ المستردة أو الإلغاء أو التنفيذ",
        ],
        outro: "Confirm that the same dates, filters, account, and metric definitions are being compared.",
        outroAr: "تأكد من مقارنة نفس التواريخ والمرشحات والحساب وتعريفات المقاييس.",
      },
      {
        question: "Why is recently generated platform data missing?",
        questionAr: "لماذا تكون بيانات المنصة الحديثة مفقودة؟",
        answer:
          "Some source platforms take time to finalise or expose data through their APIs. Allow the configured refresh to complete before comparing the latest activity.",
        answerAr:
          "تستغرق بعض منصات المصدر وقتًا لإنهاء البيانات أو إتاحتها عبر واجهاتها البرمجية. انتظر اكتمال التحديث المُهيأ قبل مقارنة أحدث نشاط.",
      },
      {
        question: "Why can another user see a feature that I cannot?",
        questionAr: "لماذا يمكن لمستخدم آخر رؤية ميزة لا أستطيع رؤيتها؟",
        answer:
          "Feature visibility may depend on your role, subscription, tenant configuration, or connected data sources. Contact your DataTram administrator to review your access.",
        answerAr:
          "قد تعتمد رؤية الميزة على دورك أو اشتراكك أو إعدادات حسابك أو مصادر البيانات المتصلة. تواصل مع مسؤول DataTram لمراجعة صلاحيات وصولك.",
      },
      {
        question: "What should I include when reporting a problem?",
        questionAr: "ماذا ينبغي أن أُدرج عند الإبلاغ عن مشكلة؟",
        answer: "Provide:",
        answerAr: "قدّم:",
        bullets: [
          "The dashboard or page name",
          "The selected date range",
          "The affected metric",
          "Any filters applied",
          "A screenshot",
          "The expected and displayed values",
          "The approximate time the issue occurred",
        ],
        bulletsAr: [
          "اسم اللوحة أو الصفحة",
          "النطاق الزمني المحدد",
          "المقياس المتأثر",
          "أي مرشحات مُطبَّقة",
          "لقطة شاشة",
          "القيم المتوقعة والمعروضة",
          "الوقت التقريبي لحدوث المشكلة",
        ],
      },
      {
        question: "How do I contact support?",
        questionAr: "كيف أتواصل مع الدعم؟",
        answer:
          "Use the in-app help widget in the lower-right corner of the screen or contact your organisation's DataTram administrator.",
        answerAr:
          "استخدم أداة المساعدة داخل التطبيق في الزاوية السفلية من الشاشة، أو تواصل مع مسؤول DataTram في مؤسستك.",
      },
    ],
  },
];
